import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import { IoCalendarOutline, IoChevronBack, IoChevronForward } from "react-icons/io5";
import { twMerge } from "tailwind-merge";
import {
  formatUsDateDisplay,
  parseIsoDateString,
  toIsoDateString,
} from "../utils/isoDate";

export interface LgvDateFieldProps {
  id?: string;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  className?: string;
}

const defaultClassNames = getDefaultClassNames();
const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

type CalendarPanel = "days" | "months" | "years";

const dayPickerClassNames = {
  root: twMerge(defaultClassNames.root, "relative p-0"),
  months: twMerge(defaultClassNames.months, "flex flex-col"),
  month: twMerge(defaultClassNames.month, "space-y-3"),
  month_caption: twMerge(
    defaultClassNames.month_caption,
    "sr-only"
  ),
  caption_label: twMerge(
    defaultClassNames.caption_label,
    "sr-only"
  ),
  nav: twMerge(defaultClassNames.nav, "hidden"),
  button_previous: twMerge(
    defaultClassNames.button_previous,
    "inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-[#1B365D] hover:bg-slate-50"
  ),
  button_next: twMerge(
    defaultClassNames.button_next,
    "inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-[#1B365D] hover:bg-slate-50"
  ),
  month_grid: twMerge(defaultClassNames.month_grid, "w-full border-collapse"),
  weekdays: twMerge(defaultClassNames.weekdays, "flex"),
  weekday: twMerge(
    defaultClassNames.weekday,
    "w-9 text-center text-xs font-medium text-[#1B365D99]"
  ),
  week: twMerge(defaultClassNames.week, "mt-1 flex w-full"),
  day: twMerge(
    defaultClassNames.day,
    "relative h-9 w-9 p-0 text-center text-sm"
  ),
  day_button: twMerge(
    defaultClassNames.day_button,
    "h-9 w-9 rounded-md font-normal text-[#1B365D] hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1B365D]/40"
  ),
  selected: twMerge(
    defaultClassNames.selected,
    "[&>button]:bg-[#1B365D] [&>button]:text-white [&>button]:hover:bg-[#152a4a]"
  ),
  today: twMerge(
    defaultClassNames.today,
    "[&>button]:font-semibold [&>button]:text-[#C1804D]"
  ),
  outside: twMerge(defaultClassNames.outside, "[&>button]:text-slate-300"),
  disabled: twMerge(
    defaultClassNames.disabled,
    "[&>button]:cursor-not-allowed [&>button]:text-slate-300 [&>button]:opacity-50"
  ),
  hidden: twMerge(defaultClassNames.hidden, "invisible"),
};

function buildDisabledMatchers(
  min?: string,
  max?: string
): Array<{ before: Date } | { after: Date }> {
  const matchers: Array<{ before: Date } | { after: Date }> = [];
  const minDate = min ? parseIsoDateString(min) : undefined;
  const maxDate = max ? parseIsoDateString(max) : undefined;
  if (minDate) matchers.push({ before: minDate });
  if (maxDate) matchers.push({ after: maxDate });
  return matchers;
}

function parseUsDateInput(value: string): Date | undefined {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim());
  if (!match) return undefined;

  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return undefined;
  }

  return date;
}

function isDateWithinBounds(date: Date, min?: string, max?: string): boolean {
  const minDate = min ? parseIsoDateString(min) : undefined;
  const maxDate = max ? parseIsoDateString(max) : undefined;

  if (minDate && date < minDate) return false;
  if (maxDate && date > maxDate) return false;
  return true;
}

function getStartMonth(min?: string, selectedDate?: Date): Date {
  const minDate = min ? parseIsoDateString(min) : undefined;
  const year = Math.min(
    minDate?.getFullYear() ?? 1900,
    selectedDate?.getFullYear() ?? Number.POSITIVE_INFINITY
  );

  return new Date(Number.isFinite(year) ? year : 1900, 0, 1);
}

function getEndMonth(max?: string, selectedDate?: Date): Date {
  const maxDate = max ? parseIsoDateString(max) : undefined;
  const year = Math.max(
    maxDate?.getFullYear() ?? new Date().getFullYear() + 20,
    selectedDate?.getFullYear() ?? Number.NEGATIVE_INFINITY
  );

  return new Date(Number.isFinite(year) ? year : new Date().getFullYear() + 20, 11, 31);
}

function getYearPageStart(year: number): number {
  return Math.floor(year / 12) * 12;
}

function getMonthWithYear(date: Date, year: number): Date {
  return new Date(year, date.getMonth(), 1);
}

function getMonthWithMonthIndex(date: Date, monthIndex: number): Date {
  return new Date(date.getFullYear(), monthIndex, 1);
}

function addCalendarMonths(date: Date, amount: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

const LgvDateField: React.FC<LgvDateFieldProps> = ({
  id,
  name,
  value,
  onChange,
  min,
  max,
  disabled = false,
  required = false,
  placeholder = "MM/DD/YYYY",
  className,
}) => {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const listboxId = `${fieldId}-calendar`;
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const selectedDate = value ? parseIsoDateString(value) : undefined;
  const displayValue = value ? formatUsDateDisplay(value) : "";
  const [inputValue, setInputValue] = useState(displayValue);
  const [month, setMonth] = useState(selectedDate ?? new Date());
  const [calendarPanel, setCalendarPanel] = useState<CalendarPanel>("days");
  const [yearPageStart, setYearPageStart] = useState(
    getYearPageStart((selectedDate ?? new Date()).getFullYear())
  );
  const disabledMatchers = buildDisabledMatchers(min, max);
  const startMonth = getStartMonth(min, selectedDate);
  const endMonth = getEndMonth(max, selectedDate);
  const startYear = startMonth.getFullYear();
  const endYear = endMonth.getFullYear();

  useEffect(() => {
    setInputValue(displayValue);
  }, [displayValue]);

  useEffect(() => {
    const nextSelectedDate = value ? parseIsoDateString(value) : undefined;
    if (nextSelectedDate) {
      setMonth(nextSelectedDate);
      setYearPageStart(getYearPageStart(nextSelectedDate.getFullYear()));
    }
  }, [value]);

  const openCalendar = useCallback(() => {
    if (disabled) return;
    setOpen(true);
  }, [disabled]);

  const closeCalendar = useCallback(() => {
    setOpen(false);
    setCalendarPanel("days");
  }, []);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        closeCalendar();
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeCalendar();
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, closeCalendar]);

  const handleSelect = (date: Date | undefined) => {
    if (!date) return;
    onChange(toIsoDateString(date));
    setInputValue(formatUsDateDisplay(toIsoDateString(date)));
    setMonth(date);
    setCalendarPanel("days");
    closeCalendar();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setInputValue(nextValue);

    if (!nextValue.trim()) {
      onChange("");
      return;
    }

    const nextDate = parseUsDateInput(nextValue);
    if (!nextDate || !isDateWithinBounds(nextDate, min, max)) return;

    onChange(toIsoDateString(nextDate));
    setMonth(nextDate);
    setYearPageStart(getYearPageStart(nextDate.getFullYear()));
  };

  const handleInputBlur = () => {
    if (!inputValue.trim()) return;

    const nextDate = parseUsDateInput(inputValue);
    if (!nextDate || !isDateWithinBounds(nextDate, min, max)) {
      setInputValue(displayValue);
      return;
    }

    setInputValue(formatUsDateDisplay(toIsoDateString(nextDate)));
  };

  const handlePreviousMonth = () => {
    const nextMonth = addCalendarMonths(month, -1);
    if (nextMonth < startMonth) return;
    setMonth(nextMonth);
    setYearPageStart(getYearPageStart(nextMonth.getFullYear()));
  };

  const handleNextMonth = () => {
    const nextMonth = addCalendarMonths(month, 1);
    if (nextMonth > endMonth) return;
    setMonth(nextMonth);
    setYearPageStart(getYearPageStart(nextMonth.getFullYear()));
  };

  const handleSelectMonth = (monthIndex: number) => {
    const nextMonth = getMonthWithMonthIndex(month, monthIndex);
    if (nextMonth < startMonth || nextMonth > endMonth) return;
    setMonth(nextMonth);
    setCalendarPanel("days");
  };

  const handleSelectYear = (year: number) => {
    const nextMonth = getMonthWithYear(month, year);
    if (nextMonth < startMonth || nextMonth > endMonth) return;
    setMonth(nextMonth);
    setYearPageStart(getYearPageStart(year));
    setCalendarPanel("days");
  };

  const canGoToPreviousMonth = addCalendarMonths(month, -1) >= startMonth;
  const canGoToNextMonth = addCalendarMonths(month, 1) <= endMonth;
  const currentMonthIndex = month.getMonth();
  const currentYear = month.getFullYear();
  const years = Array.from({ length: 12 }, (_, index) => yearPageStart + index);
  const canGoToPreviousYearPage = yearPageStart > startYear;
  const canGoToNextYearPage = yearPageStart + 11 < endYear;

  return (
    <div ref={containerRef} className={twMerge("relative w-full", className)}>
      <div
        role="combobox"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? listboxId : undefined}
        aria-disabled={disabled || undefined}
        className={twMerge(
          "flex w-full items-center rounded-md border bg-white transition-colors",
          disabled
            ? "cursor-not-allowed border-gray-200 bg-gray-100 opacity-50"
            : "border-gray-300 hover:border-[#1B365D]/40 focus-within:ring-2 focus-within:ring-blue-500"
        )}
      >
        <input
          type="text"
          id={fieldId}
          name={name}
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          inputMode="numeric"
          aria-autocomplete="none"
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={openCalendar}
          onKeyDown={(event) => {
            if (disabled) return;
            if (event.key === "Enter") {
              event.preventDefault();
              openCalendar();
            }
          }}
          className="min-w-0 flex-1 border-0 bg-transparent p-2 text-[#1B365D] outline-none placeholder:text-gray-400 disabled:cursor-not-allowed"
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          onClick={openCalendar}
          aria-label="Open calendar"
          className="mr-2 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[#1B365D] hover:bg-slate-100 disabled:cursor-not-allowed"
        >
          <IoCalendarOutline className="h-5 w-5" aria-hidden />
        </button>
      </div>

      {open && !disabled && (
        <div
          id={listboxId}
          role="dialog"
          aria-label="Choose date"
          className="absolute left-0 top-[calc(100%+4px)] z-50 w-[min(100%,19rem)] rounded-lg border border-slate-200 bg-white p-3 shadow-[0px_8px_24px_rgba(27,54,93,0.12)]"
        >
          <div className="mb-3 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={
                calendarPanel === "years"
                  ? () => setYearPageStart((year) => Math.max(startYear, year - 12))
                  : handlePreviousMonth
              }
              disabled={
                calendarPanel === "years"
                  ? !canGoToPreviousYearPage
                  : !canGoToPreviousMonth
              }
              aria-label={
                calendarPanel === "years"
                  ? "Show previous years"
                  : "Show previous month"
              }
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-[#1B365D] transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <IoChevronBack className="h-4 w-4" aria-hidden />
            </button>

            <div className="flex min-w-0 flex-1 items-center justify-center gap-2">
              <button
                type="button"
                onClick={() =>
                  setCalendarPanel((panel) => (panel === "months" ? "days" : "months"))
                }
                className={twMerge(
                  "h-9 min-w-[5.75rem] rounded-md border px-3 text-sm font-semibold transition",
                  calendarPanel === "months"
                    ? "border-[#1B365D] bg-[#1B365D] text-white"
                    : "border-slate-200 bg-white text-[#1B365D] hover:bg-slate-50"
                )}
              >
                {month.toLocaleDateString("en-US", { month: "long" })}
              </button>
              <button
                type="button"
                onClick={() => {
                  setYearPageStart(getYearPageStart(currentYear));
                  setCalendarPanel((panel) => (panel === "years" ? "days" : "years"));
                }}
                className={twMerge(
                  "h-9 min-w-[4.75rem] rounded-md border px-3 text-sm font-semibold transition",
                  calendarPanel === "years"
                    ? "border-[#1B365D] bg-[#1B365D] text-white"
                    : "border-slate-200 bg-white text-[#1B365D] hover:bg-slate-50"
                )}
              >
                {currentYear}
              </button>
            </div>

            <button
              type="button"
              onClick={
                calendarPanel === "years"
                  ? () => setYearPageStart((year) => Math.min(endYear - 11, year + 12))
                  : handleNextMonth
              }
              disabled={
                calendarPanel === "years" ? !canGoToNextYearPage : !canGoToNextMonth
              }
              aria-label={
                calendarPanel === "years" ? "Show next years" : "Show next month"
              }
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-[#1B365D] transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <IoChevronForward className="h-4 w-4" aria-hidden />
            </button>
          </div>

          {calendarPanel === "months" && (
            <div className="grid grid-cols-3 gap-2 py-1">
              {MONTH_LABELS.map((label, monthIndex) => {
                const optionMonth = getMonthWithMonthIndex(month, monthIndex);
                const isDisabled = optionMonth < startMonth || optionMonth > endMonth;
                const isSelected = monthIndex === currentMonthIndex;

                return (
                  <button
                    type="button"
                    key={label}
                    disabled={isDisabled}
                    onClick={() => handleSelectMonth(monthIndex)}
                    className={twMerge(
                      "h-10 rounded-md border text-sm font-semibold transition",
                      isSelected
                        ? "border-[#1B365D] bg-[#1B365D] text-white"
                        : "border-slate-200 text-[#1B365D] hover:bg-slate-50",
                      isDisabled && "cursor-not-allowed text-slate-300 opacity-50 hover:bg-white"
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {calendarPanel === "years" && (
            <div className="grid grid-cols-3 gap-2 py-1">
              {years.map((year) => {
                const isDisabled = year < startYear || year > endYear;
                const isSelected = year === currentYear;

                return (
                  <button
                    type="button"
                    key={year}
                    disabled={isDisabled}
                    onClick={() => handleSelectYear(year)}
                    className={twMerge(
                      "h-10 rounded-md border text-sm font-semibold transition",
                      isSelected
                        ? "border-[#1B365D] bg-[#1B365D] text-white"
                        : "border-slate-200 text-[#1B365D] hover:bg-slate-50",
                      isDisabled && "cursor-not-allowed text-slate-300 opacity-50 hover:bg-white"
                    )}
                  >
                    {year}
                  </button>
                );
              })}
            </div>
          )}

          {calendarPanel === "days" && (
            <DayPicker
              mode="single"
              selected={selectedDate}
              onSelect={handleSelect}
              month={month}
              onMonthChange={(nextMonth) => {
                setMonth(nextMonth);
                setYearPageStart(getYearPageStart(nextMonth.getFullYear()));
              }}
              hideNavigation
              startMonth={startMonth}
              endMonth={endMonth}
              disabled={disabledMatchers.length > 0 ? disabledMatchers : undefined}
              classNames={dayPickerClassNames}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default LgvDateField;
